import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ManageSubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Viewer Guide • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Managing Your Subscriptions and Notifications
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Subscriptions let you follow your favorite creators and never miss their new content. But without proper management, your subscription feed can become overwhelming. This guide shows you how to subscribe strategically, manage notifications, and keep your IndeeClips experience organized and enjoyable.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How Subscriptions Work
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When you subscribe to a channel, you're telling IndeeClips "I want to see more from this creator." Subscriptions affect your homepage recommendations and give you access to a dedicated Subscriptions feed showing all new uploads from channels you follow.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Free and unlimited:</strong> Subscribe to as many channels as you want—there's no limit</li>
          <li><strong>Private by default:</strong> Your subscriptions are private unless you choose to make them public</li>
          <li><strong>Easy to manage:</strong> Subscribe and unsubscribe anytime with one click</li>
          <li><strong>Supports creators:</strong> Subscriptions help creators grow and signal to IndeeClips that their content is valuable</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Subscribe to Channels
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          There are multiple ways to subscribe depending on where you discover content:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>From a video:</strong> Click the Subscribe button below any video player</li>
          <li><strong>From a channel page:</strong> Visit the creator's channel and click the large Subscribe button</li>
          <li><strong>From clips:</strong> Tap the creator's profile picture while watching a clip, then tap Subscribe</li>
          <li><strong>From search results:</strong> Subscribe directly from search results without visiting the channel</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Understanding Notification Levels
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Not all subscriptions are equal. IndeeClips gives you three notification levels for each channel:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          All Notifications (Bell Icon Pressed)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Get notified every time this creator uploads. Best for your absolute favorite channels that you never want to miss.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Personalized (Default)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips notifies you occasionally based on what it thinks you'll enjoy. Good for channels you like but don't need to see every upload.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          None (Bell Icon with Slash)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          No notifications—you'll only see their uploads in your Subscriptions feed. Perfect for channels you enjoy but upload too frequently.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Managing Your Subscriptions Feed
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The Subscriptions page is your personalized feed of new content from channels you follow. Here's how to make the most of it:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Access it quickly:</strong> Tap "Subscriptions" in the navigation menu (bottom bar on mobile, sidebar on desktop)</li>
          <li><strong>Newest first:</strong> Videos appear in chronological order—most recent uploads at the top</li>
          <li><strong>Mark as watched:</strong> Videos you've watched automatically move down the feed</li>
          <li><strong>Filter by channel:</strong> See uploads from specific creators you follow</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Cleaning Up Your Subscriptions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Over time, you might accumulate subscriptions to channels that no longer interest you or have stopped uploading. Regular cleanup keeps your feed relevant:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How to Unsubscribe
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>From any video:</strong> Click the "Subscribed" button below the video player—it changes to "Subscribe" when you unsubscribe</li>
          <li><strong>From channel page:</strong> Visit the channel and click "Unsubscribe"</li>
          <li><strong>From Subscriptions page:</strong> Tap the three-dot menu next to a channel and select "Unsubscribe"</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Audit Your Subscriptions Monthly
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to your Subscriptions page and sort by "Least Recent Activity"</li>
          <li>Unsubscribe from channels that haven't uploaded in 6+ months (they might be inactive)</li>
          <li>Remove channels whose content no longer interests you—your tastes change!</li>
          <li>Keep your subscription count manageable (50-100 active channels is a good target)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Notification Best Practices
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Don't Enable All Notifications for Everyone
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If you subscribe to 50 channels and enable all notifications, you'll get bombarded with alerts. Be selective:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>All notifications:</strong> Only for 3-5 must-watch channels</li>
          <li><strong>Personalized:</strong> For most subscriptions (let the algorithm decide)</li>
          <li><strong>None:</strong> For daily uploaders or channels you check manually</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Manage Device Notifications
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Control when and how you receive notifications:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Mobile:</strong> Go to phone Settings → IndeeClips → Notifications to control sound, vibration, and display</li>
          <li><strong>Desktop:</strong> Browser notification settings let you disable notifications during work hours</li>
          <li><strong>Do Not Disturb:</strong> Schedule quiet hours when notifications are muted</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Advanced Subscription Tips
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Organize by Priority
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Create a mental hierarchy of your subscriptions:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Tier 1:</strong> Must-watch channels (all notifications enabled)</li>
          <li><strong>Tier 2:</strong> Regular watches (personalized notifications)</li>
          <li><strong>Tier 3:</strong> Occasional interest (no notifications, check manually)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Subscribe to Playlists, Not Just Channels
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Some creators have multiple playlists covering different topics. Instead of subscribing to the entire channel, bookmark specific playlists you care about.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Use Watch Later for Buffer
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Don't feel pressured to watch every upload immediately. Add videos to Watch Later and consume them when you have time.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Subscription Problems (And Fixes)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Problem:</strong> "I'm subscribed but not seeing uploads." <strong>Fix:</strong> Check notification settings—you might have disabled notifications or the channel uploads infrequently.</li>
          <li><strong>Problem:</strong> "Too many notifications." <strong>Fix:</strong> Switch most channels to "Personalized" and keep "All" for only your top favorites.</li>
          <li><strong>Problem:</strong> "Subscriptions feed is overwhelming." <strong>Fix:</strong> Unsubscribe from inactive channels and daily uploaders. Keep it under 50 active subscriptions.</li>
          <li><strong>Problem:</strong> "Accidentally unsubscribed." <strong>Fix:</strong> No worries—just re-subscribe. Your viewing history makes it easy to find the channel again.</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Take Control of Your Viewing Experience
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Smart subscription management transforms IndeeClips from overwhelming to enjoyable. Subscribe strategically, set notification levels thoughtfully, and audit regularly. Your subscriptions should serve you—not stress you out.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about managing subscriptions? Contact us at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ManageSubscriptions;
